// §4.7 — ORDER DETAIL, /sales/orders/$orderId (Module 09, slice P3-C-1).
//
// What an accepted quote became, and the only screen in P3 that can answer
// "what did we promise this customer, and what is holding it up".
//
// THE TIMELINE IS BUILT FROM STAMPS THAT HAPPENED. `OrderTimelineEntry` comes
// from the §2.16 columns that are actually set, so the timeline shows
// transitions the order really made — never future steps drawn in grey, which
// is a timeline that lies about the present. The screen renders whatever
// entries the payload carries, in its order: `in_production` joined them with
// the work-order peg (D-290), stamped at the first pegged release, the shipment
// half with P3-D PR-D3.
//
// RE-PROMISE LANDED WITH P3-J J-3 (spec 09 §4.7, D-469). A holder of
// `sales.order.re_promise` (group admin, facility admin, planner) gets
// "Re-promise line {n}" under the lines table for each live line that already
// carries a date; it opens module 03's CTP dialog through `planning/public.ts`
// in its re-promise mode, which shows the old date beside the new one and asks
// whether to tell the customer (off by default). A sales user sees none of it
// and can still re-quote. Each line's chip names the promise mirror, and an
// at-risk or broken line adds the reason in words and the projected date. (Shipments and the
// qty-shipped bars LANDED with P3-D PR-D3, D-286, the proforma with PR-D4,
// D-288, and the work-order chips with the peg, D-290; each sentence left with
// its feature.)
//
// THE WORK ORDERS ARE CHIPS ON THEIR LINES (D-290). `work_orders[]` is module
// 09's own record of every work order pegged to a line of this order, written
// inside module 04's transactions, so the chips and the guards below read one
// fact. Each chip is the work order's number, its status and how much of it is
// made, in the work order's unit — operational facts only, never cost, for
// anyone who can read the order. The number links into module 04's detail ONLY
// where `openable` says that detail would answer this reader (D-118): a sales
// rep reads the chip and follows no link. A holder of `production.wo.write`
// gets "Plan a work order" on a `production` line the peg would admit (order
// confirmed / in production / ready, line open or in production), on a desktop
// — module 04's create is a desktop form (§4's mobile rule) — which opens the
// create modal there already pegged. Only while `facility_writable`, too: the
// pegged RELEASE refuses 403 a releaser whose scope does not cover the order's
// facility, so without that half a facility-scoped planner was led into a
// draft they could create and never release. A mixed line reads `stock` (the
// plan's mode quirk) and is offered no link, though the server would admit
// the peg.
//
// THE DOCUMENTS ARE PLAIN LINKS (PR-D4, D-288). The proforma, and each
// shipment's delivery note, open in a new tab straight from their doors — the
// browser follows the redirect under the session cookie (lib/documents.ts).
// Each exists only where its door answers: the proforma from `confirmed` on,
// never on a cancelled order, and only for a `sales.read_value` holder (a
// priced PDF cannot be redacted, ruling 2); a delivery note whenever one is on
// file — a shipment cancelled after its note was drawn included, since the
// door still serves it. A packed-or-later shipment with NO note on file offers
// `sales.shipment.post` holders the draw and tells anyone else, neutrally,
// that there is none: a missing note is not always a failed draw (shipments
// packed before notes existed, and the seeded ones, have none either).
//
// ATTACHED FILES (P3-E2 PR E2-2a2, spec 09 §4.7's annotation, D-323 rulings
// 8 and 10). The detail shows the order's Attached files panel
// (components/attachments/attachments-panel.tsx), with Upload and Detach for a
// holder of `sales.order.write`. Every reader the order door admits lists and
// downloads the order's technical files, qc and the storekeeper included, and
// a reader without `sales.read_value` reads how many commercial files were
// withheld.
//
// FORCE STATUS is §3.6's audited manual transition (`sales.order.override_
// status`, ga/fa; rulings 5, 8, 18, 21). It shares the ONE confirm toolbar with
// the cancel family and Mark delivered, offers ONLY the targets legal from the
// order's current status (lib/order-override.ts), takes a mandatory reason,
// and says before the press what it does and does not do — a forced `shipped`
// releases the unshipped remainder's reservations (ruling 21), and the
// sentence says so. The reason is read back on the timeline beside the stamp
// it moved, exactly as a cancel's is.
//
// THE FACILITY FENCE (D-286 ruling 25) IS A FACT ON THE READ. A
// facility-scoped admin may read another facility's order, and every door
// that decides over its shipments — the whole-order cancel, the line cancel,
// Mark delivered, Force status, opening a shipment (the ship-queue link) and
// the delivery note's re-render — answers them 403, and so does the release of
// a work order planned for it. `facility_writable` says whether the caller's
// write scope covers the order's facility, so none of those controls (nor
// Plan a work order) is offered when it is false, and one sentence says why
// instead of letting them vanish. A payload without the key (an API build
// that predates it, during a deploy roll) keeps the earlier behaviour: the
// controls show and the server's 403 stands.
//
// SHIPMENTS ARE READ HERE AND PICKED ON THE FLOOR (PR-D3, D-286). The order's
// `shipments[]` render as a register table — number, status chip, line count,
// carrier, the shipped/delivered stamps — ABSENT while the order has none (an
// empty table is not a state); each line's shipped quantity reads as
// "shipped of ordered" over a `<progress>` whose value and max are the D-041
// decimal TEXT handed to the browser verbatim (never `Number()` — the wire's
// own string is what the element parses). There is NO pick form on this
// screen: §4.8 puts open / pick / pack / ship on the floor PWA with the
// outbox, and a per-row quantity grid in this table at 412 px is exactly the
// S5 form-in-cell defect. A `sales.shipment.post` holder gets ONE link into
// the floor's ship queue while the order is shippable (§3.6's
// `confirmed / in_production / ready`), and nothing else.
//
// MARK DELIVERED is the office's door on the carrier's word (§3.6 `shipped →
// delivered`, `sales.order.deliver`): offered ONLY while the order is
// `shipped` (D-088 — the service answers 409 `ORDER_NOT_SHIPPED` anywhere
// else), confirmed in the ONE toolbar block the cancel family already uses,
// so the deliver confirm and the two cancel faces are never on the glass
// together. The customer's own press on the tracking page is the source of
// truth; this button is for the manager the carrier rang. The sales role's
// assignment half stays server-side (the same reason as the cancel control,
// below).
//
// RESERVATIONS LINK OUT TO MODULE 06. A line's `reservation_ids` are the
// physical bobbins §3.5a reserved, and the useful act on one is "show me that
// unit" — so each is a link into the inventory unit card rather than a uuid
// printed for decoration. A production line has none, and says so. A
// CANCELLED line keeps its ids as history (D-285 ruling 6) and renders them as
// text under "Released when the line was cancelled." — a link into a unit
// card that no longer holds anything for this order would be a control that
// answers a question nobody asked.
//
// CANCEL IS THE ONE WRITE FAMILY — two doors on one code since P3-D PR-D2
// (D-285): the whole-order cancel (`POST /v1/orders/{id}/cancel`, guarded
// server-side by 409 `ORDER_HAS_ACTIVE_WOS` while any line is `in_production`
// — a released or running work order holds it, D-290 ruling 4 — so the control
// is withheld then, with one sentence saying the work orders are cancelled
// first; a DRAFT work order holds nothing, ruling 5) and the LINE cancel (`POST
// /v1/orders/{id}/lines/{lineId}/cancel`, which releases that line's holds,
// recomputes the header totals over the lines that remain, and runs the
// whole-order cancel itself when the pressed line was the last non-cancelled
// one — §9.18). Both are gated here on `sales.order.cancel`, both take a
// mandatory reason, and both confirm first, because either press releases
// reservations somebody else may be counting on. The line control is a
// per-row button in an ACTIONS column whose confirm step renders in the
// toolbar BELOW the table, never in the row: a form inside a collapsed
// actions cell at 412 px became one unbreakable line with Confirm painted off
// the glass (the S5 lesson, styles/office.css), and a lone button collapses
// correctly into the phone card's labelled last row. The row gate is
// `canCancelLine` — the order gate, AND not `pending_approval` (the service
// refuses EVERY principal `ORDER_NOT_CONFIRMED` there, admins included, so
// unlike the order control there is no admin exception), AND the row's own
// status/quantity (`open`/`ready` with nothing shipped). The last-line
// consequence is disclosed BEFORE the press, beside the reason field.
//
// AND IT IS GATED ON §3.6's CANCELLABLE SET, not on "everything that is not
// over". The gate used to read "not cancelled, not closed, not delivered",
// which offered the button on a `shipped` order — a state §3.6 has no cancel
// row for, and one the service refuses with 409 `ORDER_TERMINAL`
// (orders.service.ts `CANCELLABLE`). A control whose every press is refused is
// the D-088 defect: worse than an absence, because it invites the press and
// then explains. The set below is §3.6's two cancel rows read off the spec
// table — `pending_approval` (decide → rejected, or an explicit cancel that
// withdraws the pending review) plus `confirmed, in_production, ready`.
//
// The SALES narrowing (own order, `confirmed` only — §7's cell, enforced in
// the service) is deliberately NOT mirrored here, for EITHER control: it
// needs the caller's own user id and the role feed is the only caller signal
// the client has (hooks/permissions.ts), so mirroring it would mean guessing.
// The server re-checks it either way (D-040 two layers), and the failure it
// produces is a 403 the MutationFailureNote renders — not a silent nothing.
// D-285 states this for the line control too, so nobody mirrors half of it
// for one button and not the other.
//
// ONE EXCEPTION, since the direct order (P3-D PR-D1, D-283): while an order is
// `pending_approval` the service 403s EVERY sales principal's cancel (the
// own+confirmed rule has no confirmed order to match), so that half of the
// rule IS mirrored — Cancel is offered to `perms.isAdmin` only while pending,
// and the pending banner says so. That needs no user id, only the role feed.
//
// THE PENDING STATE ITSELF (P3-D PR-D1): a direct order whose dry run flagged
// a breach or an off-list price lands here `pending_approval` with `review.
// required_approver` naming the desk (served only while pending — D-255). The
// header says who it waits for and links a decider into the inbox; each
// breaching line wears its `margin_breached` / `off_list` chip beside the
// price-source chip; the tracking card is WITHHELD (nothing is promised until
// it confirms); and the timeline carries `pending_approval` at `created_at`
// through the same status chip the list uses.
//
// THE DUPLICATE-PO NOTICE AND REQUOTE (P3-D PR-D5, D-289 rulings 4, 6-10).
// When the composer placed this order and the 201 carried `X-Sales-Warning:
// DUPLICATE_PO_REF`, the warning is handed over with the navigation and shown
// here ONCE: it names the PO and links to the customer's orders, where the
// list's Customer PO column shows which order carries it. It is neutral on
// purpose — a sales manager cannot cancel a pending order (§9.19), so the
// notice never tells them to. A reload, or a later visit, does not raise it
// again: the header was stated once and replays do not carry it.
//
// WHAT THE CUSTOMER WAS TOLD (P3-E PR-E1, D-291). Since the dispatcher, a
// status push is written `queued` by the transition and sent seconds later,
// or held for the tenant's quiet hours, or skipped; the panel names each of
// those states in words (held with its release time in the TENANT's clock,
// a failure with its reason as a sentence), and the order re-reads itself
// while a row can still move (`useOrder`). The table is the one the quote
// composer's Notifications card uses too, and lives in
// components/customer-notifications.tsx, never in either screen.
//
// Quote again (`sales-order-requote`) makes a new draft quote from this
// order's lines at today's prices and opens it in the composer carrying the
// "prices refreshed" diff, the quote's Duplicate hand-over. It is offered to
// `sales.quote.write` holders, only on a desktop (§4.4's rule for the
// Duplicate: it mints a document a phone could not then edit), and only while
// the server's `requotable` says the press would pass the target-customer
// checks: an archived customer's order, or an order a rep owns on a customer
// no longer assigned to them, stays readable and is refused every time
// (D-118). Its button lives in its own component so the mutation hook mounts
// only where the button does, and it is disabled while its request is in
// flight, so a double click makes one quote (`RequoteOrderButton`).

import { Fragment, useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Card } from "../../../../components/card";
import { formatQuantity } from "../../../../lib/quantity";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { CardSkeleton, ErrorState, ForbiddenState } from "../../../../components/states";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { useDesktop } from "../../../../components/use-desktop";
import { WholeRuns } from "../../../../components/whole-runs";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { columnPresent, formatMinor, formatMinorRuns } from "../lib/money";
import { marginVisible, priceSourceVariant } from "../lib/quotes";
import {
  canOpenProforma,
  deliveryNoteOffer,
  deliveryNotePath,
  deliveryNoteUnpriced,
  glyphCharactersSentence,
  glyphCodePointsOf,
  proformaPath,
} from "../lib/documents";
import { overrideTargets, type OverrideFacts, type OverrideTarget } from "../lib/order-override";
// Module 04's own permission mirror and chip palette: the "Plan a work order"
// link is a PRODUCTION control (`production.wo.write`), and a work order's
// status wears the variant its own screens give it.
import { deriveProductionPermissions } from "../../production/hooks/permissions";
import { productionChipVariant } from "../../production/lib/format";
import { CustomerNotificationTable } from "../components/customer-notifications";
import { OpenTasksCard } from "../components/sales-tasks";
import { MutationFailureNote } from "../mutation-failure-note";
import { canAttachTo, isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { AttachmentsPanel } from "../components/attachments/attachments-panel";
import { useOwnerIndex } from "../hooks/sales-queries";
import { useMemberRefAsk, useReadsHeldOffline } from "../lib/reads";
import {
  useArrivingOrderWarning,
  useCancelOrderLine,
  useMarkOrderDelivered,
  useOverrideOrderStatus,
  useRenderDeliveryNote,
  useRequoteOrder,
} from "../hooks/order-mutations";
import {
  handOverDuplicateDiff,
  NOTIFICATION_FAST_WINDOW_MS,
  useCancelOrder,
  useOrder,
  useRePromiseLine,
  type OrderDetail,
  type OrderLine,
} from "../hooks/quote-queries";
import { creditChipVariant, orderStatusVariant } from "./orders";
import { useProduct } from "../../catalogue/hooks/catalogue-queries";
import { CtpDialog, canRunCtp } from "../../planning/public";
import {
  lineCheckable,
  linePromiseState,
  lineRePromisable,
  openQtyText,
  orderPromisePending,
  promiseStatusVariant,
} from "../lib/promise-dates";
import { bookClientPager } from "../hooks/book-pages";

/**
 * §3.6's cancel rows, mirroring `CANCELLABLE` in
 * `apps/api/src/modules/sales/orders/orders.service.ts` — the ONE authority,
 * transcribed here so the button is offered exactly where the route accepts
 * it. Exported so the pin asserts the SET rather than re-listing it.
 *
 * `shipped` and `delivered` are absent on purpose: a shipped order is undone
 * by a return, not by a cancellation, and §3.6 gives neither a cancel row.
 */
export const CANCELLABLE: ReadonlySet<string> = new Set([
  "pending_approval",
  "confirmed",
  "in_production",
  "ready",
]);

/**
 * The LINE statuses the line cancel accepts, mirroring `LINE_CANCELLABLE` in
 * `apps/api/src/modules/sales/orders/orders.service.ts` (PR-D2, D-285 ruling
 * 4). The other three each meet a stated refusal there — `in_production`
 * 409 `LINE_HAS_ACTIVE_WOS`, `shipped` / `cancelled` 409
 * `ORDER_LINE_TERMINAL` — so the button is offered on none of them. Exported
 * so the pin asserts the SET rather than re-listing it.
 */
export const LINE_CANCELLABLE: ReadonlySet<string> = new Set(["open", "ready"]);

/**
 * The statuses a shipment may be opened against, mirroring `SHIPPABLE` in
 * `apps/api/src/modules/sales/orders/orders.service.ts` (PR-D3, D-286 ruling
 * 2): `POST /v1/orders/{id}/shipments` answers 409 `ORDER_NOT_SHIPPABLE`
 * outside it. Gates the ONE link this screen offers into the floor's ship
 * queue — never a pick form (the header explains). `ready` is written only by
 * §3.6's audited override (D-288) — nothing in production writes it yet — so
 * most stock-only orders go `confirmed → shipped`, and a forced-ready order
 * still opens a shipment here.
 */
export const SHIPPABLE: ReadonlySet<string> = new Set(["confirmed", "in_production", "ready"]);

/** One shipment as the §4.7 detail carries it (`OrderDetailResponse.shipments`). */
type ShipmentSummary = OrderDetail["shipments"][number];

/** One work-order chip as the §4.7 detail carries it (`OrderDetailResponse.
 * work_orders`, D-290): module 09's own record of a work order pegged to a line. */
type OrderWorkOrder = OrderDetail["work_orders"][number];

/**
 * The order's work-order chips. Optional HERE only, as `facility_writable` is:
 * a payload from an API build that predates the key (the deploy-roll window)
 * reads as "no work orders" rather than failing the whole document.
 */
export function orderWorkOrders(order: {
  work_orders?: readonly OrderWorkOrder[];
}): readonly OrderWorkOrder[] {
  return order.work_orders ?? [];
}

/**
 * The order statuses a work order may be pegged under, mirroring module 09's
 * peg rules (D-290 ruling 6): `pending_approval` is refused 409
 * `ORDER_NOT_CONFIRMED`, the four terminal statuses 409 `ORDER_TERMINAL`.
 * Exported so the pin asserts the SET.
 */
export const PEGGABLE_ORDER: ReadonlySet<string> = new Set(["confirmed", "in_production", "ready"]);

/** …and the LINE statuses it admits: `shipped` / `cancelled` are refused 409
 * `ORDER_LINE_TERMINAL`. Exported so the pin asserts the SET. */
export const PEGGABLE_LINE: ReadonlySet<string> = new Set(["open", "in_production"]);

/**
 * "Plan a work order" on this line (D-290): a `production` line of an order
 * and line the peg would admit, for a holder of `production.wo.write` — the
 * create's own code, so the link never leads into a 403 (D-088) — AND the
 * facility fence (`facilityWritable`; spec 09 §4.7, PR-D6 review). The create
 * accepts a planner whose scope does not cover the order's fulfilling
 * facility, but the pegged RELEASE refuses them 403 (`reason:
 * order_facility_out_of_scope`) by the same rule that serves
 * `facility_writable`, so without this half the link led into a draft that
 * reader could never release. The desktop half is the caller's (module 04's
 * create is a desktop form).
 */
export function canPlanWorkOrder(
  order: Pick<OrderDetail, "status"> & { facility_writable?: boolean },
  line: Pick<OrderLine, "fulfillment_mode" | "line_status">,
  canWriteWorkOrders: boolean,
): boolean {
  return facilityWritable(order) && linePlannable(order, line, canWriteWorkOrders);
}

/**
 * The plan link's rule WITHOUT the facility fence: would this line offer it to
 * this reader if their scope covered the order's facility? The fence sentence
 * asks exactly that (`facilityFenceWithholds`), so the two cannot drift apart.
 */
function linePlannable(
  order: Pick<OrderDetail, "status">,
  line: Pick<OrderLine, "fulfillment_mode" | "line_status">,
  canWriteWorkOrders: boolean,
): boolean {
  return (
    canWriteWorkOrders &&
    line.fulfillment_mode === "production" &&
    PEGGABLE_ORDER.has(order.status) &&
    PEGGABLE_LINE.has(line.line_status)
  );
}

/**
 * Does module 04 hold live work against this order? Mirrors the whole-order
 * cancel's 409 `ORDER_HAS_ACTIVE_WOS` in orders.service.ts, which reads the
 * LINE status and nothing else: a line is `in_production` while a pegged work
 * order is released or running (D-290 ruling 4). A draft work order does not
 * move its line (ruling 5), so it withholds nothing here either.
 */
export function orderHasActiveWorkOrders(order: {
  lines: readonly Pick<OrderLine, "line_status">[];
}): boolean {
  return order.lines.some((line) => line.line_status === "in_production");
}

/**
 * The LINE chip's variant: `shipped` is the line's DONE state and wears the ok
 * variant (PR-D3), where the ORDER's `shipped` stays accent because the order
 * is still moving (delivery is ahead of it). Every other line status reads
 * through the shared order map — `cancelled` neutral, the rest accent.
 */
function lineStatusVariant(status: string): ReturnType<typeof orderStatusVariant> {
  return status === "shipped" ? "ok" : orderStatusVariant(status);
}

/**
 * "Nothing shipped yet", read off the D-041 decimal TEXT the wire carries
 * (`qty_shipped` is `numeric(14,3)` serialised as a string, `"0.000"` on every
 * line today). A pure string test — never `Number()` on a quantity: the rule
 * this screen follows everywhere it reads a decimal.
 */
export function isZeroQty(qty: string): boolean {
  return /^0+(\.0+)?$/.test(qty);
}

/**
 * The LINE gate (D-088 — no control into a refusal), ONE predicate so the
 * pin asserts the RULE: the order gate (the code, §3.6's cancel rows), AND
 * not `pending_approval` — with NO admin exception, unlike the order control,
 * because the service answers EVERY principal 409 `ORDER_NOT_CONFIRMED` there
 * (nothing is held, the review's amount is over the stored lines, and the
 * decide's `linesForConfirm` reads every line — D-285 ruling 2) — AND the
 * row's own status and quantity: `open`/`ready` with nothing shipped (a line
 * with ANY shipped quantity is refused `ORDER_LINE_PARTIALLY_SHIPPED`, §9.18).
 * The sales role's own+confirmed half stays server-side, as for the order
 * control (the header explains why). AND the facility fence (D-286 ruling
 * 25): the line cancel refuses 403 a caller whose write scope does not cover
 * the order's facility, so `facility_writable: false` withholds it
 * (`facilityWritable`).
 */
export function canCancelLine(
  order: Pick<OrderDetail, "status"> & {
    shipments: readonly ShipmentGateFacts[];
    facility_writable?: boolean;
  },
  line: Pick<OrderLine, "line_status" | "qty_shipped">,
  perms: { has: (code: string) => boolean },
): boolean {
  return (
    perms.has("sales.order.cancel") &&
    facilityWritable(order) &&
    CANCELLABLE.has(order.status) &&
    order.status !== "pending_approval" &&
    LINE_CANCELLABLE.has(line.line_status) &&
    isZeroQty(line.qty_shipped) &&
    !order.shipments.some(shipmentBlocksLineCancel)
  );
}

/** What the two cancel gates read off one `shipments[]` entry. */
type ShipmentGateFacts = Pick<ShipmentSummary, "status" | "line_count">;

/**
 * An EMPTY open shipment — `picking` with no live pick (D-286 ruling 23). The
 * floor's pick screen opens the document on entry, so a mis-tap in the ship
 * queue leaves one behind; it holds nothing on the ledger and the service
 * neither refuses for it nor asks anyone to cancel it (it closes WITH the
 * order). Mirrors `isEmptyOpenShipment` in orders.service.ts.
 */
function isEmptyOpenShipment(shipment: ShipmentGateFacts): boolean {
  return shipment.status === "picking" && shipment.line_count === 0;
}

/**
 * The WHOLE-ORDER cancel's shipment predicate (D-286 ruling 11): any shipment
 * that is not `cancelled` refuses it with 409 `ORDER_HAS_SHIPMENTS` — an open
 * one holds picks, a shipped one means goods left — EXCEPT the empty open one.
 * D-088: the control is absent where its press would be refused, and the
 * shipments panel says why.
 */
export function shipmentBlocksOrderCancel(shipment: ShipmentGateFacts): boolean {
  return shipment.status !== "cancelled" && !isEmptyOpenShipment(shipment);
}

/** The LINE cancel's: only an OPEN shipment holding picks blocks a line — a
 * `shipped` sibling is exactly the hand-off that closes the order as shipped. */
export function shipmentBlocksLineCancel(shipment: ShipmentGateFacts): boolean {
  return (
    (shipment.status === "picking" || shipment.status === "packed") &&
    !isEmptyOpenShipment(shipment)
  );
}

/**
 * The facility fence as the screen reads it (D-286 ruling 25, served as
 * `OrderDetail.facility_writable`): does the caller's write scope cover the
 * order's facility? The key is optional HERE only so a payload from an API
 * build that predates it (the deploy-roll window) reads as writable — the
 * earlier behaviour, where the controls show and the server's 403 stands —
 * rather than as fenced with no sentence to say why.
 */
export function facilityWritable(order: { facility_writable?: boolean }): boolean {
  return order.facility_writable !== false;
}

/**
 * Would Quote again pass the target-customer checks for this caller (P3-D
 * PR-D5, D-289, served as `OrderDetail.requotable`)? False on an archived
 * customer's order (422 CUSTOMER_ARCHIVED) and on an order a sales rep owns
 * after being unassigned from its customer (404 CUSTOMER_NOT_FOUND). Optional
 * HERE only, as `facility_writable` is: a payload from an API build that
 * predates the key (the deploy-roll window) reads as requotable, and the
 * server's refusal still stands.
 */
export function orderRequotable(order: { requotable?: boolean }): boolean {
  return order.requotable !== false;
}

/**
 * Does the facility fence take away a control this reader would otherwise be
 * offered? True when the caller's write scope does not cover the order's
 * facility (`facility_writable: false`) AND they hold the code of a door the
 * order's status would open: the cancel on §3.6's cancel rows, Mark delivered
 * on a shipped order, Force status where some arc is legal, the ship queue
 * while a shipment could be opened, or Plan a work order on a line the peg
 * would admit (`plansWorkOrders`: the caller's `production.wo.write` on a
 * desktop, since the link is offered nowhere else — PR-D6 review). The
 * sentence it gates is the whole explanation, so it is said only where
 * something is actually missing — a reader holding none of those doors (a
 * sales.read-only reader, or a planner on a stock-only order) has nothing
 * withheld and reads no sentence.
 */
export function facilityFenceWithholds(
  order: Pick<OrderDetail, "status"> & {
    facility_writable?: boolean;
    lines: readonly (OverrideFacts["lines"][number] &
      Pick<OrderLine, "fulfillment_mode" | "line_status">)[];
    shipments: OverrideFacts["shipments"];
  },
  has: (code: string) => boolean,
  plansWorkOrders = false,
): boolean {
  if (facilityWritable(order)) return false;
  return (
    (has("sales.order.cancel") && CANCELLABLE.has(order.status)) ||
    (has("sales.order.deliver") && order.status === "shipped") ||
    (has("sales.order.override_status") && overrideTargets(order).length > 0) ||
    (has("sales.shipment.post") && SHIPPABLE.has(order.status)) ||
    order.lines.some((line) => linePlannable(order, line, plansWorkOrders))
  );
}

/** What the ONE confirm block below the table is about: the whole order, one
 * line, the delivery (PR-D3) or the forced status (PR-D4). Mutually exclusive
 * by construction — a union, not four booleans. */
type ConfirmTarget =
  { kind: "order" } | { kind: "line"; lineId: string } | { kind: "deliver" } | { kind: "override" };

function sameTarget(current: ConfirmTarget | null, next: ConfirmTarget): boolean {
  if (current === null) return false;
  if (current.kind === "line") return next.kind === "line" && next.lineId === current.lineId;
  return next.kind === current.kind;
}

/**
 * The override's refusal, as a sentence with its remedy (D-288 ruling 8's
 * ladder, plus ruling 21's ORDER_HAS_ACTIVE_WOS for a forced shipped over a
 * line still in production). The select offers only the arcs legal from the status the screen
 * last read, so each of these means the order moved under an open dialog —
 * and the reader needs the remedy, not the code. Each key is a LITERAL, so
 * the i18n gate checks every one; an unmapped code falls back to the verb's
 * own sentence and the note adds the server's title beneath it.
 */
function overrideFailureText(t: TFunction, error: unknown): string {
  const failed = t("sales.orders.detail.overrideFailed");
  const code = error instanceof RequestError ? error.code : undefined;
  let remedy: string | undefined;
  switch (code) {
    case "STATUS_REGRESSION":
      remedy = t("sales.orders.detail.overrideError.STATUS_REGRESSION");
      break;
    case "STATUS_OVERRIDE_NOT_ALLOWED":
      remedy = t("sales.orders.detail.overrideError.STATUS_OVERRIDE_NOT_ALLOWED");
      break;
    case "ORDER_NOTHING_SHIPPED":
      remedy = t("sales.orders.detail.overrideError.ORDER_NOTHING_SHIPPED");
      break;
    case "SHIPMENT_OPEN":
      remedy = t("sales.orders.detail.overrideError.SHIPMENT_OPEN");
      break;
    case "ORDER_TERMINAL":
      remedy = t("sales.orders.detail.overrideError.ORDER_TERMINAL");
      break;
    case "ORDER_HAS_ACTIVE_WOS":
      remedy = t("sales.orders.detail.overrideError.ORDER_HAS_ACTIVE_WOS");
      break;
    default:
      remedy = undefined;
  }
  return remedy === undefined ? failed : `${failed} ${remedy}`;
}

/** The re-render's refusal: a glyph the bundled fonts cannot draw has a remedy
 * a manager can act on — the characters, named with their U+ codes, and where
 * they live (the order's names, or the document header or footer in the sales
 * settings); anything else is the verb's sentence plus the server's own title. */
function renderFailureText(t: TFunction, error: unknown): string {
  const failed = t("sales.shipments.deliveryNoteFailed");
  if (!(error instanceof RequestError) || error.code !== "DOCUMENT_GLYPH_COVERAGE") return failed;
  const characters = glyphCharactersSentence(t, glyphCodePointsOf(error));
  return `${failed} ${characters} ${t("sales.shipments.deliveryNoteGlyphs")}`;
}

/** The requote's refusal: "every line dropped" has a sentence of its own,
 * because it is the one a manager meets on an old order whose products left
 * the lists; anything else is the verb's sentence plus the server's title. */
function requoteFailureText(t: TFunction, error: unknown): string {
  return error instanceof RequestError && error.code === "QUOTE_NO_LINES"
    ? t("sales.orders.detail.requoteNoLines")
    : t("sales.orders.detail.requoteFailed");
}

/**
 * Quote again (§5 `POST /v1/orders/{id}/requote`, D-289 rulings 6-10). On
 * success the §9.3 diff travels to the new quote exactly as the quote's
 * Duplicate sends it — `sourceNo` is this ORDER's number, and the labels are
 * keyed by the order's line numbers, because `dropped[].line_no` counts the
 * order's lines (the route's contract) — and the composer opens the quote.
 *
 * The screen's one-note rule: `onPress` clears every other door's failure
 * note, and this note shows only while `failureEpoch` is still the one it was
 * pressed under — any other door's press moves the epoch and hides it.
 *
 * ONE REQUEST PER PRESS. Each press mints its own Idempotency-Key, so a second
 * press would be a second quote, not a replay. `busy={requote.isPending}`
 * disables the button until the request settles: the press's own state
 * updates re-render the button in the same event, and that render reads the
 * mutation's pending state, so the second click of a double click lands on a
 * disabled button.
 */
function RequoteOrderButton({
  order,
  failureEpoch,
  onPress,
}: {
  order: OrderDetail;
  failureEpoch: number;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const requote = useRequoteOrder(order.id);
  const [pressedUnder, setPressedUnder] = useState<number | undefined>(undefined);
  return (
    <>
      <div className="sales-toolbar">
        <Button
          size="sm"
          data-testid="sales-order-requote"
          busy={requote.isPending}
          busyLabel={t("sales.orders.detail.requoting")}
          onClick={() => {
            onPress();
            setPressedUnder(failureEpoch);
            requote.mutate(
              {},
              {
                onSuccess: (result) => {
                  handOverDuplicateDiff(result.quote_id, {
                    sourceNo: order.order_no,
                    repriced: result.repriced,
                    dropped: result.dropped,
                    labels: Object.fromEntries(
                      order.lines.map((line) => [line.line_no, line.description]),
                    ),
                  });
                  void navigate({
                    to: "/sales/quotes/$quoteId",
                    params: { quoteId: result.quote_id },
                  });
                },
              },
            );
          }}
        >
          {t("sales.orders.detail.requote")}
        </Button>
      </div>
      {requote.isError && pressedUnder === failureEpoch ? (
        <MutationFailureNote
          data-testid="sales-order-requote-failed"
          text={requoteFailureText(t, requote.error)}
          error={requote.error}
        />
      ) : null}
    </>
  );
}

/** The sentence that says what the forced status does and does not do, per
 * target: a forced ready or shipped messages the customer, a forced close
 * messages nobody (D-288 ruling 12). */
function overrideBody(t: TFunction, target: OverrideTarget): string {
  switch (target) {
    case "ready":
      return t("sales.orders.detail.overrideBody.ready");
    case "shipped":
      return t("sales.orders.detail.overrideBody.shipped");
    case "closed":
      return t("sales.orders.detail.overrideBody.closed");
  }
}

export function SalesOrderDetailScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const params = useParams({ strict: false });
  const orderId = typeof params.orderId === "string" ? params.orderId : "";

  const refused = isSalesReadRefused(perms, perms.has("sales.read"));
  // Check date on an undated line (spec 09 §4.7, D-465): the line the CTP
  // dialog is open on, and the lines a commit from it dated whose stamp has
  // not landed yet (module 09 stamps the line when module 03's event
  // arrives), so the detail keeps re-reading until it has.
  const [checkLineId, setCheckLineId] = useState<string | undefined>(undefined);
  const [rePromiseLineId, setRePromiseLineId] = useState<string | undefined>(undefined);
  const [awaitingStamp, setAwaitingStamp] = useState<ReadonlySet<string>>(new Set());
  const detail = useOrder(refused ? "" : orderId, { awaitingStamp: awaitingStamp.size > 0 });
  const owners = useOwnerIndex(perms.isAdmin);
  // A member directory the browser holds offline stays pending for the whole
  // cut, so the owner reads as its short id until it answers, the way the
  // 360 and the peek read it (#293).
  const directoryOffline = useReadsHeldOffline();
  const ownersResolving = owners.isPending && !directoryOffline;
  const memberRef = useMemberRefAsk(ownersResolving);
  const cancel = useCancelOrder(orderId);
  const cancelLine = useCancelOrderLine(orderId);
  const deliver = useMarkOrderDelivered(orderId);
  const override = useOverrideOrderStatus(orderId);
  const renderNote = useRenderDeliveryNote(orderId);
  const isDesktop = useDesktop();
  // Module 04's mirror over the SAME role feed the sales mirror read — no
  // second request, and fail-closed alike (no role codes, no link).
  const canWriteWorkOrders = deriveProductionPermissions(perms).has("production.wo.write");
  // The create's warning, if the composer handed one over for THIS order.
  const arriving = useArrivingOrderWarning(orderId);

  const [confirming, setConfirming] = useState<ConfirmTarget | null>(null);
  const [reason, setReason] = useState("");
  // The override's chosen target. "" = not chosen yet, which reads as the
  // first legal target — re-derived on every render, so a choice the payload
  // no longer admits falls back instead of confirming a refusal.
  const [overrideChoice, setOverrideChoice] = useState<OverrideTarget | "">("");
  const [copied, setCopied] = useState(false);
  // Moves on every reset below; the requote's note shows only under the epoch
  // it was pressed in (RequoteOrderButton).
  const [failureEpoch, setFailureEpoch] = useState(0);

  // Every write on this screen resets every OTHER write's failure note, so at
  // most one note is on the glass and it is the current claim
  // (mutation-failure-note.tsx's idiom).
  const resetDoorFailures = () => {
    cancel.reset();
    cancelLine.reset();
    deliver.reset();
    override.reset();
    renderNote.reset();
  };
  const resetFailures = () => {
    resetDoorFailures();
    setFailureEpoch((epoch) => epoch + 1);
  };

  // ONE confirm block, ONE target. Pressing a row's Cancel line, the order's
  // Cancel, Mark delivered or Force status re-targets the toolbar; re-pressing
  // the CURRENT target is a no-op (the typed reason survives), and any other
  // press starts a fresh reason and clears EVERY door's failure note — at most
  // one note on the glass, and it is always the current claim
  // (mutation-failure-note.tsx's idiom).
  const target = (next: ConfirmTarget) => {
    if (sameTarget(confirming, next)) return;
    setConfirming(next);
    setReason("");
    setOverrideChoice("");
    resetFailures();
  };

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  // A line whose stamp has landed stops holding the poll open.
  useEffect(() => {
    const lines = detail.data?.lines;
    if (lines === undefined || awaitingStamp.size === 0) return;
    const landed = lines.filter((l) => awaitingStamp.has(l.id) && l.line_promise_date !== null);
    if (landed.length === 0) return;
    setAwaitingStamp((prior) => {
      const next = new Set(prior);
      for (const l of landed) next.delete(l.id);
      return next;
    });
  }, [detail.data, awaitingStamp]);

  if (refused) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.orders")} />;
  }
  if (detail.isPending) {
    return <CardSkeleton lines={8} label={t("sales.common.loading")} />;
  }
  const order = detail.data;
  // The error card is for an order we could not load AT ALL. Once one is on
  // the glass, a failed background re-read — the §2.20 poll re-reads this
  // detail every few seconds while a message is going out (useOrder), and a
  // deploy roll can fail one of those reads — leaves the order readable: the
  // next read replaces it, and blanking a document someone is working in
  // (a cancel reason half typed) over a missed refresh is the worse failure.
  if (order === undefined) {
    return (
      <ErrorState
        title={t("sales.orders.detail.error")}
        retryLabel={t("sales.common.retry")}
        requestIdLabel={t("sales.common.requestId")}
        requestId={requestIdOf(detail.error)}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  const money = (minor: number) => formatMinor(minor, order.currency, i18n.language);
  // A line's money as two whole runs, amount and currency, so the `fit` lines
  // table may put the currency under the amount (design-system §3.3, D-336).
  const moneyRuns = (minor: number) => (
    <WholeRuns runs={formatMinorRuns(minor, order.currency, i18n.language)} />
  );
  // §7 omission on the line money: the columns exist iff at least one line
  // carried the keys, and a rendered column's absent cell renders nothing.
  const showLineMoney = columnPresent(order.lines, (line) => line.unit_price_minor);
  // The two §2.17 pricing facts §7 redacts by OMISSION (P3-D PR-D1, D-283):
  // `list_price_minor` rides `sales.read_value`, the margin block rides
  // `sales.margin.read`. Each column exists iff at least one line carried the
  // key — `null` counts as carried (a hand-priced line has no list price and
  // says so with an empty cell, not a missing column).
  const showListPrice = columnPresent(order.lines, (line) => line.list_price_minor);
  const showMargin = columnPresent(order.lines, (line) => line.margin_pct);
  // A DIRECT order waiting for a decision (§3.6, P3-D PR-D1). `review` is
  // served only while pending (D-255's shape), so its presence IS the state;
  // the status is checked as well so a payload that carried both by mistake
  // cannot paint the banner on a confirmed order.
  const pending = order.status === "pending_approval" && order.review !== undefined;
  // THE FACILITY FENCE (D-286 ruling 25, served as `facility_writable`): every
  // door below that decides over the order's shipments refuses 403 a caller
  // whose write scope does not cover the order's facility, so each gate reads
  // it (`facilityWritable` — a payload that predates the key keeps the earlier
  // behaviour, as the header explains).
  const writable = facilityWritable(order);
  // Plan a work order counts only where the link could ever be offered: a
  // `production.wo.write` holder on a desktop (module 04's create is a
  // desktop form, so a phone withholds nothing the fence took).
  const fenceWithholds = facilityFenceWithholds(
    order,
    (code) => perms.has(code),
    isDesktop && canWriteWorkOrders,
  );
  // §3.6's cancel rows, AND the pending narrowing (D-088): the service refuses
  // a sales principal's cancel on any non-confirmed order with 403
  // (orders.service.ts), so while `pending_approval` the control is offered to
  // an admin only — a button whose every press 403s is worse than an absence.
  // The `isAdmin` test is the role feed (ga/fa), the only caller signal the
  // client has; the service re-checks either way (D-040 two layers).
  const cancelByStatus =
    perms.has("sales.order.cancel") &&
    writable &&
    CANCELLABLE.has(order.status) &&
    (order.status !== "pending_approval" || perms.isAdmin);
  // …AND the shipments (PR-D3, D-286 rulings 11 + 23): the service refuses the
  // whole-order cancel 409 `ORDER_HAS_SHIPMENTS` while a shipment holds picks
  // or goods have left, and while any line carries shipped quantity (its belt).
  // `cancelBlockedByShipments` is the state in which the control WOULD be here
  // but for a shipment — the one case where an absence needs a sentence. The
  // shipments panel carries it when there is a panel; when the array is empty
  // the actions area carries it instead (auditor S5, below).
  const cancelBlockedByShipments =
    cancelByStatus &&
    (order.shipments.some(shipmentBlocksOrderCancel) ||
      order.lines.some((line) => !isZeroQty(line.qty_shipped)));
  // …AND the work orders (D-290): while a line is `in_production` the service
  // refuses 409 `ORDER_HAS_ACTIVE_WOS`, so the control is withheld and one
  // sentence (below the tables) says the work orders are cancelled first.
  const cancelBlockedByWorkOrders = cancelByStatus && orderHasActiveWorkOrders(order);
  const canCancel = cancelByStatus && !cancelBlockedByShipments && !cancelBlockedByWorkOrders;
  // The LINE gate, per row (PR-D2, D-285). The actions column exists only
  // when SOME row takes the press — a pending or terminal order shows no
  // empty "Actions" header. Until PR-D3 `canCancelLine` implied `canCancel`
  // (its order half was the stricter one). It no longer does: on a PARTLY
  // SHIPPED order the whole-order cancel is refused (goods left) while a line
  // that has not shipped may still leave the document — the hand-off that
  // closes the order as shipped (D-286 ruling 9). So the confirm block below
  // renders for a line target on its own, not only inside `canCancel`.
  const lineCancellable = order.lines.some((line) => canCancelLine(order, line, perms));
  // The target is RE-DERIVED from the current payload on every render rather
  // than trusted from state: a line that stopped being cancellable under an
  // open confirm (a colleague's cancel refetched in) drops the block instead
  // of offering a Confirm the service would refuse (D-088).
  const targetLineId = confirming?.kind === "line" ? confirming.lineId : undefined;
  const lineTarget =
    targetLineId === undefined
      ? undefined
      : order.lines.find((line) => line.id === targetLineId && canCancelLine(order, line, perms));
  // §9.18 disclosed BEFORE the press, in TWO sentences (PR-D2 + PR-D3):
  //   * the target is the last NON-CANCELLED line → the press cancels the
  //     whole order (D-285);
  //   * every OTHER non-cancelled line is already `shipped` → the press closes
  //     the order as SHIPPED through the ship path's own transition body
  //     (D-286 ruling 9, D-285 ruling 11 discharged) — never as cancelled,
  //     because a shipped sibling is billed and keeps the order alive.
  // "Open" is the reader's word for the target's own state (it is cancellable,
  // so `open`/`ready`); the sentence chosen depends on the SIBLINGS.
  const siblingsAlive =
    lineTarget === undefined
      ? []
      : order.lines.filter((line) => line.id !== lineTarget.id && line.line_status !== "cancelled");
  const targetIsLast = lineTarget !== undefined && siblingsAlive.length === 0;
  const targetClosesShipped =
    lineTarget !== undefined &&
    siblingsAlive.length > 0 &&
    siblingsAlive.every((line) => line.line_status === "shipped");
  // MARK DELIVERED (PR-D3, D-286 ruling 14): offered only where the service
  // honours it — `sales.order.deliver` AND `shipped`. Anywhere else the door
  // answers 409 (`ORDER_TERMINAL` / `ORDER_NOT_SHIPPED`), so the button is
  // withheld rather than offered and explained (D-088). The sales role's
  // assignment half is the service's D-224 404, as for the cancel control.
  const canDeliver = perms.has("sales.order.deliver") && writable && order.status === "shipped";
  // Re-derived from the payload like the line target: a deliver confirm left
  // open while a colleague's press (or the customer's) refetched the order
  // as `delivered` drops the face instead of offering a Yes the service
  // would refuse.
  const deliverTarget = confirming?.kind === "deliver" && canDeliver;
  // §4.8's ONE pick UI is on the floor; the office offers a link into its ship
  // queue to a `sales.shipment.post` holder while a shipment can be opened —
  // and only where they may open one (the shipment `open` door carries the
  // same facility fence).
  const canOpenShipQueue =
    perms.has("sales.shipment.post") && writable && SHIPPABLE.has(order.status);
  // THE TRACKING LINK FOLLOWS THE TOKEN (D-337 ruling 2). `public_token` is
  // served only to a holder of `sales.read_value`, because the page it opens
  // can show the order's prices and carries the customer's own
  // confirm-received press. For any other reader the key is absent and so is
  // the card, with no sentence, as the Total column is under D-239 ruling 2:
  // printing `/track/undefined` and offering to copy it would hand on a link
  // to nothing.
  const trackingPath =
    order.public_token === undefined ? undefined : `/track/${order.public_token}`;
  // FORCE STATUS (PR-D4, D-288): the code, AND at least one arc legal from the
  // current status — an order with nowhere to be forced shows no button
  // (D-088). The targets are re-derived from the payload on every render, and
  // so is the open dialog: a colleague's press that refetched the order into
  // a status with no arc drops the face instead of offering a refused Confirm.
  // The facility fence withholds it too (`writable`, above).
  const overrideOptions =
    perms.has("sales.order.override_status") && writable ? overrideTargets(order) : [];
  const canOverride = overrideOptions.length > 0;
  const overrideOpen = confirming?.kind === "override" && canOverride;
  const overrideSelected: OverrideTarget | undefined =
    overrideChoice !== "" && overrideOptions.includes(overrideChoice)
      ? overrideChoice
      : overrideOptions[0];
  // The proforma (PR-D4, D-288 rulings 2, 19): confirmed onward, never on a
  // cancelled order, and only for a reader who may see the prices it prints.
  const showProforma = canOpenProforma(order.status, (code) => perms.has(code));
  // The manager's reason for each forced step, keyed by the status it forced
  // (rows are oldest first, so a later row for the same status wins — which
  // cannot happen, since an order only moves forward, but the map says so).
  const overrideReasons = new Map(
    order.status_overrides.map((row) => [row.to_status, row.reason] as const),
  );
  // The delivery-note column exists only when some row has something to show:
  // an order whose shipments are all still picking (or cancelled with no note)
  // gets no empty column. The draw is fenced like every write here.
  const noteOffers = new Map(
    order.shipments.map(
      (shipment) =>
        [shipment.id, deliveryNoteOffer(shipment, (code) => perms.has(code), writable)] as const,
    ),
  );
  const showNoteColumn = [...noteOffers.values()].some((offer) => offer !== "none");
  // Which copy the link opens is the reader's, not the shipment's (D-337
  // ruling 4): the stored note for a holder of `sales.read_value`, the copy
  // without prices for anyone else.
  const notesUnpriced = deliveryNoteUnpriced((code) => perms.has(code));

  // The work-order chips by line (D-290), in the order the payload carries
  // them. The Production column exists when the order has any chip or any
  // `production` line — a stock-only order with no work order gets no empty
  // column.
  const workOrdersByLine = new Map<string, OrderWorkOrder[]>();
  for (const workOrder of orderWorkOrders(order)) {
    const bucket = workOrdersByLine.get(workOrder.sales_order_line_id);
    if (bucket === undefined) workOrdersByLine.set(workOrder.sales_order_line_id, [workOrder]);
    else bucket.push(workOrder);
  }
  const planWorkOrderOn = (line: OrderLine) =>
    isDesktop && canPlanWorkOrder(order, line, canWriteWorkOrders);
  const showProduction =
    workOrdersByLine.size > 0 || order.lines.some((line) => line.fulfillment_mode === "production");

  // Both tables go through the register's ONE table primitive — no hand-rolled
  // markup, which is the D-107 gate and also the reason these rows collapse to
  // labelled cards on a phone without this screen owning any CSS for it. They
  // are read-only, so no sort/select wave props: a caret on a document's own
  // lines would reorder the LINE NUMBERS a customer's PDF is printed in.
  const qty = (value: string): string => formatQuantity(value, { locale: i18n.language });
  // Check date is a desktop verb on this screen, as every other line write is.
  const canCheckLines = canRunCtp(perms) && isDesktop;
  const showPromiseColumn = order.lines.some(
    (line) =>
      line.line_promise_date !== null ||
      (line.promise_status ?? null) !== null ||
      awaitingStamp.has(line.id) ||
      linePromiseState(order, line) === "checking",
  );
  const checkableLines = canCheckLines
    ? order.lines.filter((line) => lineCheckable(order, line) && !awaitingStamp.has(line.id))
    : [];
  const checkLine =
    checkLineId === undefined ? undefined : order.lines.find((line) => line.id === checkLineId);
  // Re-promise (D-469): the code, the dialog's own code, and a desktop.
  const canRePromiseLines = perms.has("sales.order.re_promise") && canRunCtp(perms) && isDesktop;
  const rePromiseLines = canRePromiseLines
    ? order.lines.filter((line) => lineRePromisable(order, line) && !awaitingStamp.has(line.id))
    : [];
  const rePromiseLine =
    rePromiseLineId === undefined
      ? undefined
      : order.lines.find((line) => line.id === rePromiseLineId);
  // D-560 ruling 3: eight columns at most, from fourteen. A fact that
  // qualifies another sits under it in the same cell: the price source and the
  // two breach chips under the product, the ordered quantity in the shipped
  // cell, the unit price, list price and margin under the line total, and the
  // reserved units under the fulfilment mode. Every test id stays on its row.
  const lineColumns: DataTableColumn<OrderLine>[] = [
    {
      key: "description",
      header: t("sales.orders.detail.colProduct"),
      id: true,
      render: (line) => (
        <div className="sales-cell-stack">
          <span>{line.description}</span>
          {/* The §2.17 provenance and breach facts every reader gets (P3-D
              PR-D1, D-283): the source chip through the composer's own
              variant map, then the two boolean chips. `margin_breached` is the
              one margin fact a reader without `sales.margin.read` is entitled
              to, and `off_list` is §2.14's analytics fact. Booleans are never
              §7-redacted, so these chips exist for every reader. */}
          <span className="sales-cell-chips">
            <StatusChip
              variant={priceSourceVariant(line.price_source)}
              dot={line.price_source === "manual"}
              data-testid="sales-price-source"
              data-source={line.price_source}
            >
              {line.price_source === "offcut_class"
                ? t("sales.priceSource.offcutPlain")
                : t(`sales.priceSource.${line.price_source}`)}
            </StatusChip>
            {line.margin_breached ? (
              <StatusChip variant="warn" data-testid="sales-order-line-needs-approval">
                {t("sales.orders.detail.lineNeedsApproval")}
              </StatusChip>
            ) : null}
            {line.off_list ? (
              <StatusChip variant="bad" data-testid="sales-order-line-off-list">
                {t("sales.orders.detail.lineOffList")}
              </StatusChip>
            ) : null}
          </span>
        </div>
      ),
    },
    {
      key: "qty_shipped",
      kind: "qty",
      header: t("sales.orders.detail.colShipped"),
      numeric: true,
      // §4.7's "qty shipped progress bars" (PR-D3), with the ordered quantity
      // in the same sentence ("200 of 400"), so the line needs no separate
      // quantity column. The bar reads the D-041 decimal TEXT: `<progress>`
      // takes a floating-point string for `value` and `max`, so the wire's
      // own "200.000" / "400.000" go to the element verbatim. A cancelled row
      // keeps the sentence and loses the bar, which would read "still to ship".
      render: (line) =>
        line.line_status === "cancelled" ? (
          // A cancelled line keeps the bare shipped number, with what was
          // ordered under it: "0 of 5" would read as five still to ship.
          <div className="sales-cell-stack">
            <span className="num" data-testid="sales-order-line-qty">
              {qty(line.qty_shipped)}
            </span>
            <span className="sales-cell-sub">
              <span className="sales-hint">{t("sales.orders.detail.colQty")}</span>{" "}
              <span className="num">{qty(line.qty_ordered)}</span>
            </span>
          </div>
        ) : (
          <div className="sales-cell-stack">
            <span className="num" data-testid="sales-order-line-qty">
              {t("sales.orders.detail.progressOf", {
                shipped: qty(line.qty_shipped),
                ordered: qty(line.qty_ordered),
              })}
            </span>
            <progress
              className="sales-order-progress"
              data-testid="sales-order-line-progress"
              value={line.qty_shipped}
              max={line.qty_ordered}
            />
          </div>
        ),
    },
    // A line's promise date (D-349 ruling 1, D-465): typed, imported or
    // stamped by module 03's commit, with the promise chip from module 09's
    // own mirror (`promise_status`). An undated line reads "Promise date being
    // checked" while the order's Confirmed message waits for its dates. The
    // column appears once any line has something to show in it; Check date
    // sits under the table, so the column never widens a table that has no
    // date to show (the D-336 width budget).
    ...(showPromiseColumn
      ? [
          {
            key: "line_promise_date",
            header: t("sales.orders.detail.colPromise"),
            render: (line: OrderLine) => {
              const state = awaitingStamp.has(line.id) ? "checking" : linePromiseState(order, line);
              return (
                <>
                  {line.line_promise_date !== null ? (
                    <span className="num" data-testid="sales-order-line-promise">
                      {formatBusinessDate(line.line_promise_date, i18n.language)}
                    </span>
                  ) : null}
                  {line.promise_status != null ? (
                    <>
                      {" "}
                      <StatusChip
                        variant={promiseStatusVariant(line.promise_status)}
                        dot={false}
                        data-testid="sales-order-line-promise-chip"
                        data-status={line.promise_status}
                      >
                        {t(`sales.orders.detail.promiseStatus.${line.promise_status}`)}
                      </StatusChip>
                    </>
                  ) : null}
                  {state === "checking" && line.line_promise_date === null ? (
                    <span className="sales-hint" data-testid="order-line-promise-pending">
                      {t("sales.orders.detail.promiseChecking")}
                    </span>
                  ) : null}
                  {/* At risk: the reason in words and the projected date;
                      broken keeps the reason it last carried (J-3). */}
                  {(line.promise_status === "at_risk" || line.promise_status === "broken") &&
                  (line.promise_risk_reason ?? null) !== null ? (
                    <span
                      className="sales-hint"
                      data-testid="sales-order-line-promise-risk"
                      data-reason={line.promise_risk_reason ?? undefined}
                    >
                      {" "}
                      {t(`sales.orders.detail.promiseRisk.${line.promise_risk_reason ?? ""}`)}
                    </span>
                  ) : null}
                  {line.promise_status === "at_risk" &&
                  (line.promise_projected_date ?? null) !== null ? (
                    <span
                      className="num sales-valid-urgent"
                      data-testid="sales-order-line-promise-projected"
                      data-date={line.promise_projected_date ?? undefined}
                    >
                      {" "}
                      {t("sales.orders.detail.promiseProjected", {
                        date: formatBusinessDate(line.promise_projected_date ?? "", i18n.language),
                      })}
                    </span>
                  ) : null}
                </>
              );
            },
          } satisfies DataTableColumn<OrderLine>,
        ]
      : []),
    // §7 omission: the money cell EXISTS only when the payload carried a
    // price, and each line in it only when its own key arrived: the unit
    // price and the list price for a `sales.read_value` holder, the margin for
    // a `sales.margin.read` holder (`marginVisible` decides per line, on key
    // presence, the composer's rule). An absent value renders nothing, never
    // a dash.
    ...(showLineMoney || showListPrice || showMargin
      ? [
          {
            key: "line_total_minor",
            header: t("sales.orders.detail.colTotal"),
            numeric: true,
            render: (line: OrderLine) => (
              <div className="sales-cell-stack">
                {line.line_total_minor === undefined ? null : (
                  <span className="num" data-testid="sales-order-line-total">
                    {moneyRuns(line.line_total_minor)}
                  </span>
                )}
                {line.unit_price_minor === undefined ? null : (
                  <span className="sales-cell-sub" data-testid="sales-order-line-unit-price">
                    <span className="sales-hint">{t("sales.orders.detail.colPrice")}</span>{" "}
                    <span className="num">{moneyRuns(line.unit_price_minor)}</span>
                  </span>
                )}
                {/* `null` is a carried key ("no list priced this line"), so
                    the cell keeps its place for the other lines and this
                    line says nothing: a label with no value under it would
                    read as a price that failed to load. */}
                {showListPrice && line.list_price_minor != null ? (
                  <span className="sales-cell-sub" data-testid="sales-order-line-list-price">
                    <span className="sales-hint">{t("sales.orders.detail.colListPrice")}</span>{" "}
                    <span className="num">{moneyRuns(line.list_price_minor)}</span>
                  </span>
                ) : null}
                {showMargin && marginVisible(line) && line.margin_pct != null ? (
                  <span className="sales-cell-sub">
                    <span className="sales-hint">{t("sales.orders.detail.colMargin")}</span>{" "}
                    <span className="num" data-testid="sales-order-line-margin">
                      {t("sales.composer.marginPct", { pct: line.margin_pct })}
                      {/* A space, not only the qualifier's margin: without it
                          "49.12%Порог" was one unbreakable run that held the
                          column 104 px wide, and read as one word aloud. */}
                      {line.margin_guard_pct != null ? " " : null}
                      {line.margin_guard_pct != null ? (
                        <span className="sales-provisional">
                          {t("sales.composer.marginGuard", { pct: line.margin_guard_pct })}
                        </span>
                      ) : null}
                    </span>
                  </span>
                ) : null}
              </div>
            ),
          } satisfies DataTableColumn<OrderLine>,
        ]
      : []),
    {
      key: "fulfillment_mode",
      header: t("sales.orders.detail.colMode"),
      // The mode, then the stock it holds under it: the reserved units as
      // links, a produced line's "nothing reserved", or a cancelled line's
      // released units as text.
      render: (line) => (
        <div className="sales-cell-stack">
          <span>{t(`sales.orders.detail.mode.${line.fulfillment_mode}`)}</span>
          <span className="sales-cell-sub">
            {line.line_status === "cancelled" ? (
              // A cancelled line KEEPS its ids as history (D-285 ruling 6): the
              // holds read `released` in module 06 and are named on the audit
              // row and the event, so they stay on the glass, as TEXT, never as
              // links into a unit card that no longer holds anything for this
              // order. `noReservations` ("this line is produced") would be false
              // for a cancelled stock line, so the released sentence takes the
              // cell whatever the array holds.
              <>
                <span className="sales-hint">{t("sales.orders.detail.reservationsReleased")}</span>
                {line.reservation_ids.map((id, index) => (
                  <Fragment key={id}>
                    {" "}
                    <span data-testid="sales-order-reservation" data-released="true">
                      {t("sales.orders.detail.reservation", { n: index + 1 })}
                    </span>
                  </Fragment>
                ))}
              </>
            ) : line.reservation_ids.length === 0 ? (
              <span className="sales-hint">{t("sales.orders.detail.noReservations")}</span>
            ) : (
              <>
                {line.reservation_ids.map((id, index) => (
                  <Fragment key={id}>
                    {index > 0 ? " " : null}
                    <a href={`/inventory/units/${id}`} data-testid="sales-order-reservation">
                      {t("sales.orders.detail.reservation", { n: index + 1 })}
                    </a>
                  </Fragment>
                ))}
              </>
            )}
          </span>
        </div>
      ),
    },
    {
      key: "line_status",
      header: t("sales.orders.detail.colLineStatus"),
      // The NEUTRAL variant for `cancelled` is `orderStatusVariant`'s closed
      // fact (orders.tsx), shared with the order chip; no strike-through on
      // the row — the line's numbers are history a reader may still need. A
      // `shipped` LINE is done and wears ok (`lineStatusVariant`, PR-D3).
      render: (line) => (
        <StatusChip
          variant={lineStatusVariant(line.line_status)}
          dot={false}
          data-testid="sales-order-line-status"
          data-status={line.line_status}
        >
          {t(`sales.orders.detail.lineStatus.${line.line_status}`)}
        </StatusChip>
      ),
    },
    // §4.7's work-order chips (D-290), one per work order pegged to the line,
    // then "Plan a work order" where the reader may make one. Addressable by
    // work-order id and status for the walkthrough (M09-S20 reads both).
    ...(showProduction
      ? [
          {
            key: "work_orders",
            header: t("sales.orders.detail.colProduction"),
            render: (line: OrderLine) => {
              const chips = workOrdersByLine.get(line.id) ?? [];
              const plan = planWorkOrderOn(line);
              return (
                <>
                  {chips.map((workOrder) => (
                    <div
                      key={workOrder.work_order_id}
                      data-testid="sales-order-wo-chip"
                      data-wo-id={workOrder.work_order_id}
                      data-status={workOrder.status}
                    >
                      {/* The link exists only where module 04's detail
                          answers this reader (`openable`, D-118); the number
                          is the same text either way. */}
                      {workOrder.openable ? (
                        <Link
                          to="/production/work-orders/$id"
                          params={{ id: workOrder.work_order_id }}
                          data-testid="sales-order-wo-link"
                        >
                          {workOrder.wo_no}
                        </Link>
                      ) : (
                        <span>{workOrder.wo_no}</span>
                      )}{" "}
                      <StatusChip variant={productionChipVariant(workOrder.status)} dot={false}>
                        {t(`production.status.wo.${workOrder.status}`)}
                      </StatusChip>{" "}
                      <span className="num">
                        {t("sales.orders.detail.woMade", {
                          good: workOrder.qty_good,
                          planned: workOrder.qty_planned,
                          uom: workOrder.uom,
                        })}
                      </span>
                    </div>
                  ))}
                  {chips.length === 0 &&
                  line.fulfillment_mode === "production" &&
                  PEGGABLE_LINE.has(line.line_status) ? (
                    <span className="sales-hint">{t("sales.orders.detail.noWorkOrders")}</span>
                  ) : null}
                  {plan ? (
                    <div>
                      <Link
                        to="/production/work-orders"
                        search={{ peg_order_id: order.id, peg_line_id: line.id }}
                        data-testid="sales-order-line-plan-wo"
                        data-line-id={line.id}
                      >
                        {t("sales.orders.detail.planWorkOrder")}
                      </Link>
                    </div>
                  ) : null}
                </>
              );
            },
          } satisfies DataTableColumn<OrderLine>,
        ]
      : []),
    // The per-line Cancel (PR-D2, D-285) — the precedent is the overrides
    // panel's revoke column (components/customer-overrides.tsx). The button is
    // the ONLY thing in the cell; the confirm step is the toolbar's below the
    // table (see the header), so this cell collapses into the phone card's
    // labelled last row as a lone ≥44 px control.
    ...(lineCancellable
      ? [
          {
            key: "actions",
            header: t("sales.orders.detail.colActions"),
            actions: true,
            render: (line: OrderLine) =>
              canCancelLine(order, line, perms) ? (
                <Button
                  size="sm"
                  variant="danger"
                  data-testid="sales-order-line-cancel"
                  onClick={() => {
                    target({ kind: "line", lineId: line.id });
                  }}
                >
                  {t("sales.orders.detail.lineCancel")}
                </Button>
              ) : null,
          } satisfies DataTableColumn<OrderLine>,
        ]
      : []),
  ];

  // §4.7's shipments panel (PR-D3, D-286): the order's shipment DOCUMENTS as
  // the register's table, one row per `shipments[]` entry. No money anywhere
  // — `ShipmentSummary` carries none (§7: the shipment surface shows
  // quantities only), so this table exists identically for every reader who
  // can open the order. Rows are addressable by shipment id and status for
  // the walkthrough (M09-S19 reads `data-status`).
  const shipmentColumns: DataTableColumn<ShipmentSummary>[] = [
    { key: "shipment_no", header: t("sales.orders.detail.colShipmentNo"), id: true },
    {
      key: "status",
      header: t("sales.orders.detail.colShipmentStatus"),
      // `shipped` accent (in transit), `delivered` ok (done), the rest
      // neutral: the same reading `orderStatusVariant` gives the order,
      // applied to the document that carries the goods.
      render: (shipment) => (
        <StatusChip
          variant={
            shipment.status === "delivered"
              ? "ok"
              : shipment.status === "shipped"
                ? "accent"
                : "neutral"
          }
          dot={false}
          data-testid="sales-shipment-status"
          data-status={shipment.status}
        >
          {t(`sales.shipments.status.${shipment.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "line_count",
      header: t("sales.orders.detail.colShipmentLines"),
      numeric: true,
      render: (shipment) => <span className="num">{shipment.line_count}</span>,
    },
    {
      key: "carrier",
      header: t("sales.orders.detail.colCarrier"),
      // Carrier and tracking reference are the ship press's own free text
      // (their first writer is `POST /v1/shipments/{id}/ship`); both optional,
      // so an absent pair renders nothing rather than a dash that invites
      // "why is this empty?".
      render: (shipment) => {
        const parts: string[] = [];
        if (shipment.carrier !== null) parts.push(shipment.carrier);
        if (shipment.tracking_ref !== null) parts.push(shipment.tracking_ref);
        return parts.length === 0 ? null : <span>{parts.join(" · ")}</span>;
      },
    },
    {
      key: "shipped_at",
      header: t("sales.orders.detail.colShippedAt"),
      age: true,
      render: (shipment) =>
        shipment.shipped_at === null ? null : (
          <span className="num">{formatTimestamp(shipment.shipped_at, i18n.language)}</span>
        ),
    },
    {
      key: "delivered_at",
      header: t("sales.orders.detail.colDeliveredAt"),
      age: true,
      render: (shipment) =>
        shipment.delivered_at === null ? null : (
          <span className="num">{formatTimestamp(shipment.delivered_at, i18n.language)}</span>
        ),
    },
    // The delivery note (PR-D4, D-288 rulings 10, 11, 14): a link whenever a
    // note is on file; the draw for a `sales.shipment.post` holder whose
    // scope covers the facility when a packed-or-later row has none; a
    // neutral sentence for anyone else. The door is `sales.read`, so every
    // reader of this panel may open the link, and since D-337 a reader without
    // `sales.read_value` opens the copy without prices under a label that says
    // so. One control per cell, so the
    // phone card collapses it into a labelled row.
    ...(showNoteColumn
      ? [
          {
            key: "delivery_note_file_id",
            header: t("sales.orders.detail.colDeliveryNote"),
            render: (shipment: ShipmentSummary) => {
              const offer = noteOffers.get(shipment.id) ?? "none";
              if (offer === "link") {
                return (
                  <a
                    className="ds-lnk"
                    href={deliveryNotePath(shipment.id)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`sales-shipment-note-${shipment.shipment_no}`}
                    data-unpriced={notesUnpriced ? "true" : undefined}
                  >
                    {notesUnpriced
                      ? t("sales.shipments.deliveryNoteUnpriced")
                      : t("sales.shipments.deliveryNote")}
                  </a>
                );
              }
              if (offer === "render") {
                return (
                  <Button
                    size="sm"
                    busy={renderNote.isPending && renderNote.variables === shipment.id}
                    busyLabel={t("sales.shipments.deliveryNoteRendering")}
                    disabled={renderNote.isPending}
                    data-testid={`sales-shipment-note-render-${shipment.shipment_no}`}
                    onClick={() => {
                      resetFailures();
                      renderNote.mutate(shipment.id);
                    }}
                  >
                    {t("sales.shipments.deliveryNoteRender")}
                  </Button>
                );
              }
              if (offer === "missing") {
                return (
                  <span
                    className="sales-hint"
                    data-testid={`sales-shipment-note-missing-${shipment.shipment_no}`}
                  >
                    {t("sales.shipments.deliveryNoteMissing")}
                  </span>
                );
              }
              return null;
            },
          } satisfies DataTableColumn<ShipmentSummary>,
        ]
      : []),
  ];

  return (
    <section className="sales-order-detail" data-testid="sales-order-detail">
      <PageHeader
        title={order.order_no}
        crumbs={[{ label: t("sales.orders.detail.crumbRoot"), to: "/sales/orders" }]}
      />

      <div className="sales-360-header">
        <div className="sales-360-idline">
          <a href={`/sales/customers/${order.customer_id}`}>{order.customer_display_name}</a>
          <StatusChip
            variant={orderStatusVariant(order.status)}
            data-testid="sales-order-status"
            data-status={order.status}
          >
            {t(`sales.orders.status.${order.status}`)}
          </StatusChip>
          {order.credit_check_status !== "passed" ? (
            <StatusChip
              variant={creditChipVariant(order.credit_check_status)}
              data-testid="sales-order-credit"
              data-credit={order.credit_check_status}
            >
              {t(`sales.orders.credit.${order.credit_check_status}`)}
            </StatusChip>
          ) : null}
          {/* §4.6's credit headline, on the document it is about. §7 omission,
              never null: the serializer emits the key only for a
              `blocked_credit` order read by a `sales.read_value` holder
              (orders.serializer.ts), so `undefined` is "you may not see this"
              AND "there is nothing over" at once — and both render nothing.
              The sentence is `sales.reviews.creditOver` rather than a second
              wording of the same fact, the same reuse the Pricing tab makes of
              `sales.reviews.lineBreach`. */}
          {order.credit_over_limit_minor !== undefined ? (
            <span className="num" data-testid="sales-order-credit-over">
              {t("sales.reviews.creditOver", {
                amount: money(order.credit_over_limit_minor),
              })}
            </span>
          ) : null}
          {/* The header total re-renders from the response after a line
              cancel recomputes it (PR-D2) — the testid makes the recompute
              assertable on the glass as well as on the wire. §7 omission
              stands: no key, no node. */}
          {order.total_minor !== undefined ? (
            <span className="num" data-testid="sales-order-total">
              {money(order.total_minor)}
            </span>
          ) : null}
          {order.quote_no !== null ? (
            <a
              href={order.quote_id === null ? undefined : `/sales/quotes/${order.quote_id}`}
              data-testid="sales-order-quote-link"
            >
              {t("sales.orders.detail.quoteLink", { no: order.quote_no })}
            </a>
          ) : null}
          {order.customer_po_ref !== null ? (
            <span>
              {t("sales.orders.detail.poRef")}: {order.customer_po_ref}
            </span>
          ) : null}
          {/* §4.7: an order the open orders lane loaded says so to every
              reader, and links its job for a reader served the id (D-353
              ruling 5). */}
          {order.imported === true ? (
            <span className="sales-hint" data-testid="sales-order-imported">
              {order.imported_from_job_id != null ? (
                <Link
                  to="/sales/orders/import/$importId"
                  params={{ importId: order.imported_from_job_id }}
                  data-testid="sales-order-imported-link"
                >
                  {t("sales.orders.detail.imported")}
                </Link>
              ) : (
                t("sales.orders.detail.imported")
              )}
            </span>
          ) : null}
          <RefLabel
            value={owners.byId.get(order.owner_user_id)}
            id={order.owner_user_id}
            pending={ownersResolving}
            {...memberRef}
          />
        </div>
        {/* §4.7 while `pending_approval` (P3-D PR-D1, D-283): the header
            names WHICH DESK the order waits for, from the server's own
            `review.required_approver` — never re-derived here from the
            lines, which would be a second copy of §3.4's threshold rule. The
            link into the inbox is offered only to a principal who can open
            it (a decide code holder); the note that the placing manager
            cannot withdraw the order is §9 item 19, said beside the state it
            is about rather than discovered as a 403. */}
        {pending && order.review !== undefined ? (
          <p
            className="sales-banner"
            role="status"
            data-testid="sales-order-review-pending"
            data-approver={order.review.required_approver}
          >
            {t("sales.orders.detail.reviewPending", {
              approver: t(`sales.orders.detail.approver.${order.review.required_approver}`),
            })}{" "}
            {perms.canOpenReviews ? (
              <Link to="/sales/reviews" data-testid="sales-order-open-reviews">
                {t("sales.orders.detail.openReviews")}
              </Link>
            ) : null}
            {!perms.isAdmin ? (
              <span className="sales-hint" data-testid="sales-order-cancel-admin-only">
                {" "}
                {t("sales.orders.detail.cancelAdminOnly")}
              </span>
            ) : null}
          </p>
        ) : null}
        {/* The create's warning, once, on the order it is about (D-289
            rulings 4, 10). Neutral: it names the PO and opens the customer's
            orders, never "cancel this one". */}
        {arriving.code !== undefined ? (
          <p
            className="sales-banner"
            role="status"
            data-testid="sales-order-duplicate-po"
            data-code={arriving.code}
          >
            {arriving.code === "DUPLICATE_PO_REF"
              ? t("sales.orders.detail.duplicatePo", { po: order.customer_po_ref ?? "" })
              : t("sales.orders.detail.warningUnknown", { code: arriving.code })}{" "}
            {arriving.code === "DUPLICATE_PO_REF" ? (
              <Link
                to="/sales/orders"
                search={{ customer: order.customer_id }}
                data-testid="sales-order-duplicate-po-link"
              >
                {t("sales.orders.detail.duplicatePoLink")}
              </Link>
            ) : null}{" "}
            <Button
              size="sm"
              data-testid="sales-order-duplicate-po-dismiss"
              onClick={arriving.dismiss}
            >
              {t("sales.common.close")}
            </Button>
          </p>
        ) : null}
      </div>

      <Card
        className="sales-360-card"
        data-testid="sales-order-timeline"
        title={t("sales.orders.detail.timeline")}
      >
        <Card.Body>
          <ol className="sales-timeline">
            {order.timeline.map((entry) => (
              <li key={`${entry.status}:${entry.at}`} data-testid="sales-timeline-entry">
                <StatusChip variant={orderStatusVariant(entry.status)} dot={false}>
                  {t(`sales.orders.status.${entry.status}`)}
                </StatusChip>
                <span className="num">{formatTimestamp(entry.at, i18n.language)}</span>
                {/* WHY it was cancelled, beside WHEN. The reason is mandatory on
                  the cancel route and was until now written to the order and
                  never read back — a manager could cancel with a reason and
                  find nothing on the document that carried it. No label: the
                  chip beside it already says "Cancelled", and the text is the
                  manager's OWN words, so a label would be the only English on
                  a Russian document. */}
                {entry.status === "cancelled" && order.cancelled_reason !== null ? (
                  <span className="sales-hint" data-testid="sales-order-cancelled-reason">
                    {order.cancelled_reason}
                  </span>
                ) : null}
                {/* WHY it was forced, beside WHEN (PR-D4, D-288 ruling 20): the
                  manager's own words from `status_overrides`, verbatim and
                  unlabelled, for the same reason the cancel reason above is. */}
                {overrideReasons.has(entry.status) ? (
                  <span
                    className="sales-hint"
                    data-testid="sales-timeline-override-reason"
                    data-status={entry.status}
                  >
                    {overrideReasons.get(entry.status)}
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
          {/* The promise date, labelled, so the timeline's stamps and this date
            never read as two unexplained dates (audit #13). */}
          {order.promise_date !== null || (order.at_risk_reason ?? null) !== null ? (
            <p
              className="sales-facts-line"
              data-testid="sales-order-promise"
              data-at-risk-reason={order.at_risk_reason ?? undefined}
            >
              {order.promise_date !== null ? (
                <>
                  <span className="sales-hint">{t("sales.orders.detail.promised")}</span>{" "}
                  <span
                    className={
                      (order.at_risk_reason ?? null) !== null ? "num sales-valid-urgent" : "num"
                    }
                  >
                    {formatBusinessDate(order.promise_date, i18n.language)}
                  </span>
                </>
              ) : null}
              {(order.at_risk_reason ?? null) !== null ? (
                <>
                  {" "}
                  <StatusChip
                    variant={order.at_risk_reason === "promise_broken" ? "bad" : "warn"}
                    dot={false}
                    data-testid="sales-order-at-risk"
                    data-reason={order.at_risk_reason ?? undefined}
                  >
                    {t(`sales.orders.atRiskReason.${order.at_risk_reason ?? ""}`)}
                  </StatusChip>
                </>
              ) : null}
            </p>
          ) : null}
          {/* The §4.7 pending that lives on the timeline: only while the
              order has no date and can still get one (J-K). */}
          {orderPromisePending(order) ? (
            <p className="sales-pending-note" data-testid="sales-order-promise-pending">
              {t("sales.orders.detail.promisePending")}
            </p>
          ) : null}
        </Card.Body>
      </Card>

      {/* The lines sit in a Card titled like every other section, so the
          title is the 13 px card head and never a bare h2 larger than the
          page's own title (audit #13). */}
      <Card title={t("sales.orders.detail.linesTitle")} data-testid="sales-order-lines-card">
        <DataTable
          clientPager={bookClientPager(t, i18n.language)}
          data-testid="sales-order-lines"
          columns={lineColumns}
          rows={order.lines}
          rowKey={(line) => line.id}
          empty={null}
          caption={t("sales.orders.detail.linesTitle")}
          rowTestId="sales-order-line"
          // Rows are addressable by LINE ID and status (PR-D2): a walkthrough
          // that pressed "the second row" would press whichever line the seed
          // happened to order second.
          rowProps={(line) => ({
            "data-mode": line.fulfillment_mode,
            "data-line-id": line.id,
            "data-line-status": line.line_status,
          })}
          // WRAP BEFORE WIDEN (design-system §3.3). Up to twelve columns for an
          // admin, and in Russian the seeded order's lines held the table
          // 1252 px wide at a 1440 px viewport, with Cancel line past the glass
          // (W-SWEEP, fresh replica). With `fit` and the margin cell's own break
          // the table's narrowest width was 1183 px, and the widest seeded order
          // later measured 1200 px. With D-336's gutter and the money cells' two
          // runs it is 1127 px, inside its 1188 px box.
          fit
        />
        {/* Check date for each undated line of a confirmed order (spec 09
            §4.7, D-465): module 03's CTP dialog bound to the line, whose
            Commit dates it. Under the table, one button per line. */}
        {/* Re-promise for each dated live line (spec 09 §4.7, D-469), for a
            holder of sales.order.re_promise, beside Check date's buttons. */}
        {rePromiseLines.length > 0 ? (
          <div className="sales-order-check-dates" data-testid="sales-order-re-promise-lines">
            {rePromiseLines.map((line) => (
              <Button
                key={line.id}
                size="sm"
                data-testid="sales-order-line-repromise"
                data-line-id={line.id}
                onClick={() => {
                  setRePromiseLineId(line.id);
                }}
              >
                {t("sales.orders.detail.rePromiseLine", { line: line.line_no })}
              </Button>
            ))}
          </div>
        ) : null}
        {checkableLines.length > 0 ? (
          <div className="sales-order-check-dates" data-testid="sales-order-check-dates">
            {checkableLines.map((line) => (
              <Button
                key={line.id}
                size="sm"
                data-testid="sales-order-line-check-date"
                data-line-id={line.id}
                onClick={() => {
                  setCheckLineId(line.id);
                }}
              >
                {t("sales.orders.detail.checkDateLine", { line: line.line_no })}
              </Button>
            ))}
          </div>
        ) : null}
      </Card>
      {checkLine !== undefined && canCheckLines ? (
        <OrderLineCheckDialog
          line={checkLine}
          facilityId={order.fulfilling_facility_id}
          label={t("sales.orders.detail.lineLabel", {
            order: order.order_no,
            line: checkLine.line_no,
          })}
          onCommitted={(lineId) => {
            setAwaitingStamp((prior) => new Set(prior).add(lineId));
            // The stamp normally lands within seconds; the poll stops after
            // the fast window either way.
            window.setTimeout(() => {
              setAwaitingStamp((prior) => {
                const next = new Set(prior);
                next.delete(lineId);
                return next;
              });
            }, NOTIFICATION_FAST_WINDOW_MS);
            void detail.refetch();
          }}
          onClose={() => {
            setCheckLineId(undefined);
          }}
        />
      ) : null}
      {rePromiseLine !== undefined && canRePromiseLines ? (
        <OrderLineRePromiseDialog
          orderId={order.id}
          line={rePromiseLine}
          facilityId={order.fulfilling_facility_id}
          label={t("sales.orders.detail.lineLabel", {
            order: order.order_no,
            line: rePromiseLine.line_no,
          })}
          onClose={() => {
            setRePromiseLineId(undefined);
          }}
        />
      ) : null}
      {/* The ONE way into the pick UI from here (§4.8): a link to the floor's
          ship queue for a `sales.shipment.post` holder while a shipment can
          be opened. A plain anchor, like this screen's other cross-area links
          (a unit card, a customer): the floor PWA is its own shell, and a
          full navigation into it is the honest one. */}
      {canOpenShipQueue ? (
        <p className="sales-hint">
          <a href={`/floor/ship/${order.id}`} data-testid="sales-order-open-ship-queue">
            {t("sales.orders.detail.openShipQueue")}
          </a>
        </p>
      ) : null}

      {/* §4.7's shipments panel (PR-D3): ABSENT while the order has no
          shipment — an empty table is not a state, and the lines table's
          progress cells already say "0.000 of 100.000". */}
      {order.shipments.length > 0 ? (
        <Card
          className="sales-360-card"
          data-testid="sales-order-shipments"
          title={t("sales.orders.detail.shipmentsTitle")}
        >
          {cancelBlockedByShipments ? (
            <p className="sales-hint" data-testid="sales-order-cancel-blocked-by-shipments">
              {t("sales.orders.detail.cancelBlockedByShipments")}
            </p>
          ) : null}
          <DataTable
            clientPager={bookClientPager(t, i18n.language)}
            data-testid="sales-order-shipment-table"
            columns={shipmentColumns}
            rows={order.shipments}
            rowKey={(shipment) => shipment.id}
            empty={null}
            caption={t("sales.orders.detail.shipmentsTitle")}
            rowTestId="sales-order-shipment"
            rowProps={(shipment) => ({
              "data-shipment-id": shipment.id,
              "data-status": shipment.status,
              "data-delivered-by": shipment.delivered_by ?? undefined,
            })}
          />
          {/* The re-render's refusal, beside the table whose press made it. */}
          {renderNote.isError ? (
            <MutationFailureNote
              data-testid="sales-mutation-failed"
              text={renderFailureText(t, renderNote.error)}
              error={renderNote.error}
            />
          ) : null}
        </Card>
      ) : null}
      {/* The proforma invoice (PR-D4, D-288), where its stated pending stood:
          rendered on demand in the customer's language, so it is always the
          order as it stands now. */}
      {showProforma ? (
        <p className="sales-hint">
          <a
            className="ds-lnk"
            href={proformaPath(order.id)}
            target="_blank"
            rel="noreferrer"
            data-testid="sales-order-proforma"
          >
            {t("sales.orders.detail.proforma")}
          </a>
        </p>
      ) : null}
      {/* Quote again (D-289 rulings 6, 10): `sales.quote.write` holders,
          desktop only, and only where the server says the press would pass
          the target-customer checks (`requotable`). */}
      {perms.has("sales.quote.write") && isDesktop && orderRequotable(order) ? (
        <RequoteOrderButton order={order} failureEpoch={failureEpoch} onPress={resetDoorFailures} />
      ) : null}

      {order.fulfillment_degradations.length > 0 ? (
        <Card
          className="sales-360-card"
          data-testid="sales-order-degraded"
          title={t("sales.orders.detail.degradedTitle")}
        >
          <ul>
            {order.fulfillment_degradations.map((entry) => (
              <li key={`${entry.line_no}:${entry.occurred_at}`}>
                {t("sales.orders.detail.degradedLine", {
                  line: entry.line_no,
                  planned: t(`sales.orders.detail.mode.${entry.planned_mode}`),
                  actual: t(`sales.orders.detail.mode.${entry.actual_mode}`),
                })}
                {entry.impact_minor !== undefined ? (
                  <span className="num">
                    {" "}
                    {t("sales.orders.detail.degradedImpact", {
                      amount: money(entry.impact_minor),
                    })}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* Attached files (E2-2a2, spec 09 §4.7's annotation, D-323 rulings 8
          and 10), with Upload and Detach for a holder of `sales.order.write`
          (the order door that loaded this order admits it). qc and the
          storekeeper, who hold the order door, list and download its
          technical files, because drawings and certificates serve inspection
          and packing; a commercial file is counted for them and never shown
          (§7 narrowing 19). The detail serves no customer status, so an
          archived customer's refusal reads under Upload as the server's 422. */}
      <AttachmentsPanel
        subject={{ kind: "sales_order", id: order.id }}
        canWrite={canAttachTo(perms, "sales_order")}
      />

      <Card
        className="sales-360-card"
        data-testid="sales-order-notifications"
        title={t("sales.orders.detail.notificationsTitle")}
      >
        {/* The Confirmed message waits for the delivery dates (D-465 ruling
            3): no row exists yet, and this says so instead of "nothing has
            gone out". It clears when the row is written. */}
        {(order.confirmed_notice_due_at ?? null) !== null ? (
          <p className="sales-hint" data-testid="order-confirmed-notice-pending">
            {t("sales.orders.detail.confirmedNoticePending")}
          </p>
        ) : null}
        <CustomerNotificationTable
          rows={order.notifications}
          clientPager={bookClientPager(t, i18n.language)}
          tableTestId="sales-order-notification-table"
          rowTestId="sales-order-notification"
          caption={t("sales.orders.detail.notificationsTitle")}
          empty={
            <p className="sales-hint" data-testid="sales-order-notifications-empty">
              {t("sales.orders.detail.notifEmpty")}
            </p>
          }
        />
      </Card>

      {/* The order's OPEN TASKS (P3-E PR-E2, D-292 ruling 1), under what the
          customer was told: a `NO_CHANNEL` task is the manager's half of a
          status message that could not go out, and the two read together.
          The card gates itself on `sales.activity.write` and reads the
          reader's own tasks unless they are an admin
          (components/sales-tasks.tsx). */}
      <OpenTasksCard
        subject={{ kind: "order", id: order.id, customerId: order.customer_id }}
        label={order.order_no}
      />

      {/* WITHHELD while `pending_approval` (P3-D PR-D1): the public token is
          minted at creation, so the page would resolve — to five unreached
          steps about an order nobody has promised yet. The office does not
          hand a customer a link to nothing; the card appears the moment the
          order confirms. */}
      {pending || trackingPath === undefined ? null : (
        <Card
          className="sales-360-card"
          data-testid="sales-order-tracking"
          title={t("sales.orders.detail.trackingTitle")}
        >
          {/* The link itself is a token nobody reads: the card says what the
              page is for and offers Copy and Open, never the path as text. */}
          <p className="sales-hint">{t("sales.orders.detail.trackingHint")}</p>
          <div className="sales-tracking-actions">
            <Button
              size="sm"
              data-testid="sales-order-tracking-copy"
              onClick={() => {
                // `navigator.clipboard` is TYPED as always present and is genuinely
                // absent in an insecure context, so the widened local is the honest
                // type — and it is what makes the guard a real check rather than the
                // dead `?.` the linter (correctly) refuses. Same idiom as
                // areas/admin/components/recovery-codes.tsx.
                const { clipboard } = navigator as unknown as { clipboard?: Clipboard };
                if (clipboard === undefined) return;
                void clipboard.writeText(`${window.location.origin}${trackingPath}`).then(
                  () => {
                    setCopied(true);
                  },
                  // A clipboard the browser refused is not an error worth a card:
                  // the link is on the glass and can be copied by hand.
                  () => undefined,
                );
              }}
            >
              {copied
                ? t("sales.orders.detail.trackingCopied")
                : t("sales.orders.detail.trackingCopy")}
            </Button>
            <a
              href={trackingPath}
              target="_blank"
              rel="noreferrer"
              data-testid="sales-order-tracking-open"
            >
              {t("sales.orders.detail.trackingOpen")}
            </a>
          </div>
        </Card>
      )}

      {cancel.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.orders.detail.cancelFailed")}
          error={cancel.error}
        />
      ) : null}
      {cancelLine.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.orders.detail.lineCancelFailed")}
          error={cancelLine.error}
        />
      ) : null}
      {deliver.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.orders.detail.deliverFailed")}
          error={deliver.error}
        />
      ) : null}
      {override.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={overrideFailureText(t, override.error)}
          error={override.error}
        />
      ) : null}

      {/* The same sentence, in the actions area, when there is no panel to
          carry it: `cancelBlockedByShipments` true through the `qty_shipped`
          arm with an EMPTY `shipments` array. That state was the fenced
          reader's (a facility-scoped caller outside the order's facility
          reads its shipments through 0063's RESTRICTIVE fence, so they are
          invisible while the refusal stands); since `facility_writable` that
          reader is offered no Cancel at all and reads the fence sentence
          below instead, and this copy stays the belt for any payload whose
          lines say goods left while no shipment row came with it. Same key
          and same testid as the panel's copy; the array's length makes the
          two exclusive, so exactly one is ever on the glass. D-088 holds
          either way — no Cancel control is rendered in this state. */}
      {cancelBlockedByShipments && order.shipments.length === 0 ? (
        <p className="sales-hint" data-testid="sales-order-cancel-blocked-by-shipments">
          {t("sales.orders.detail.cancelBlockedByShipments")}
        </p>
      ) : null}

      {/* Why Cancel is absent while module 04 works on a line (D-290): the
          service answers 409 ORDER_HAS_ACTIVE_WOS, so the control is withheld
          and this says what lifts it. Said beside the shipments sentence when
          both hold: each names its own remedy. */}
      {cancelBlockedByWorkOrders ? (
        <p className="sales-hint" data-testid="sales-order-cancel-blocked-by-wos">
          {t("sales.orders.detail.cancelBlockedByWos")}
        </p>
      ) : null}

      {/* The facility fence (D-286 ruling 25): the controls it withholds are
          absent, and this says why — only when one of them would otherwise be
          on the glass for this reader. */}
      {fenceWithholds ? (
        <p className="sales-hint" data-testid="sales-order-facility-fenced">
          {t("sales.orders.detail.facilityFenced")}
        </p>
      ) : null}

      {/* ONE toolbar, five faces: the LINE confirm (a row's Cancel line was
          pressed — the sentence names the line, the last-line disclosure
          precedes the field), the ORDER confirm, the DELIVER confirm (PR-D3),
          the OVERRIDE confirm (PR-D4), or the idle buttons — Cancel while
          cancellable, Mark delivered while shipped, Force status while some
          arc is legal; the block is rendered when any of them has a press to
          offer. The line confirm lives HERE and not in the row for the reason
          the header gives (the S5 form-in-cell lesson). */}
      {canCancel || canDeliver || canOverride || lineTarget !== undefined ? (
        <div
          className={lineTarget !== undefined ? "sales-toolbar sales-line-cancel" : "sales-toolbar"}
          data-testid="sales-order-cancel-block"
        >
          {lineTarget !== undefined ? (
            <>
              <span className="sales-hint" data-testid="sales-order-line-cancel-target">
                {t("sales.orders.detail.lineCancelTarget", {
                  n: lineTarget.line_no,
                  product: lineTarget.description,
                })}
              </span>
              {targetIsLast || targetClosesShipped ? (
                <span
                  className="sales-hint"
                  data-testid="sales-order-line-cancel-last"
                  // WHICH consequence the press has: the order cancels with its
                  // last line, or closes as SHIPPED because every survivor has
                  // shipped (D-286 ruling 9). One testid, two sentences — the
                  // attribute is what a test (and a screen reader's author)
                  // can tell them apart by without reading a translation.
                  data-kind={targetIsLast ? "last" : "last-shipped"}
                >
                  {targetIsLast
                    ? t("sales.orders.detail.lineCancelLast")
                    : t("sales.orders.detail.lineCancelLastShipped")}
                </span>
              ) : null}
              <Field label={t("sales.orders.detail.lineCancelReason")} wide>
                <input
                  value={reason}
                  data-testid="sales-order-line-cancel-reason"
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </Field>
              <Button
                variant="danger"
                size="sm"
                disabled={reason.trim().length === 0}
                busy={cancelLine.isPending}
                busyLabel={t("sales.orders.detail.lineCancelling")}
                data-testid="sales-order-line-cancel-confirm"
                onClick={() => {
                  const trimmed = reason.trim();
                  if (trimmed.length === 0) return;
                  cancelLine.mutate(
                    { lineId: lineTarget.id, reason: trimmed },
                    {
                      onSuccess: () => {
                        setConfirming(null);
                        setReason("");
                      },
                    },
                  );
                }}
              >
                {t("sales.orders.detail.lineCancelConfirm")}
              </Button>
              <Button
                size="sm"
                data-testid="sales-order-line-cancel-keep"
                disabled={cancelLine.isPending}
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                  cancelLine.reset();
                }}
              >
                {t("sales.orders.detail.lineCancelKeep")}
              </Button>
            </>
          ) : deliverTarget ? (
            <>
              <span className="sales-hint" data-testid="sales-order-deliver-title">
                {t("sales.orders.detail.deliverTitle")}
              </span>
              <Button
                variant="primary"
                size="sm"
                busy={deliver.isPending}
                data-testid="sales-order-deliver-confirm"
                onClick={() => {
                  deliver.mutate(undefined, {
                    onSuccess: () => {
                      setConfirming(null);
                    },
                  });
                }}
              >
                {t("sales.orders.detail.deliverConfirm")}
              </Button>
              <Button
                size="sm"
                data-testid="sales-order-deliver-keep"
                disabled={deliver.isPending}
                onClick={() => {
                  setConfirming(null);
                  deliver.reset();
                }}
              >
                {t("sales.orders.detail.deliverKeep")}
              </Button>
            </>
          ) : overrideOpen && overrideSelected !== undefined ? (
            <>
              <span className="sales-hint" data-testid="sales-order-override-title">
                {t("sales.orders.detail.overrideTitle")}
              </span>
              <Field label={t("sales.orders.detail.overrideTarget")}>
                <Select
                  value={overrideSelected}
                  data-testid="sales-order-override-target"
                  onChange={(e) => {
                    const next = overrideOptions.find((option) => option === e.target.value);
                    if (next !== undefined) setOverrideChoice(next);
                  }}
                >
                  {overrideOptions.map((option) => (
                    <option key={option} value={option}>
                      {t(`sales.orders.status.${option}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              {/* What the press does and does not do, BEFORE the press, and
                  per target: a forced close messages nobody. */}
              <span
                className="sales-hint"
                data-testid="sales-order-override-body"
                data-target={overrideSelected}
              >
                {overrideBody(t, overrideSelected)}
              </span>
              <Field label={t("sales.orders.detail.overrideReason")} wide>
                <input
                  value={reason}
                  maxLength={500}
                  data-testid="sales-order-override-reason"
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </Field>
              <Button
                variant="primary"
                size="sm"
                disabled={reason.trim().length === 0}
                busy={override.isPending}
                busyLabel={t("sales.orders.detail.overriding")}
                data-testid="sales-order-override-confirm"
                onClick={() => {
                  const trimmed = reason.trim();
                  if (trimmed.length === 0) return;
                  override.mutate(
                    { target_status: overrideSelected, reason: trimmed },
                    {
                      onSuccess: () => {
                        setConfirming(null);
                        setReason("");
                        setOverrideChoice("");
                      },
                    },
                  );
                }}
              >
                {t("sales.orders.detail.overrideConfirm")}
              </Button>
              <Button
                size="sm"
                data-testid="sales-order-override-keep"
                disabled={override.isPending}
                onClick={() => {
                  setConfirming(null);
                  setReason("");
                  setOverrideChoice("");
                  override.reset();
                }}
              >
                {t("sales.orders.detail.overrideKeep")}
              </Button>
            </>
          ) : confirming?.kind === "order" && canCancel ? (
            <>
              <Field label={t("sales.orders.detail.cancelReason")} wide>
                <input
                  value={reason}
                  data-testid="sales-order-cancel-reason"
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </Field>
              <Button
                variant="danger"
                size="sm"
                disabled={reason.trim().length === 0}
                busy={cancel.isPending}
                busyLabel={t("sales.orders.detail.cancelling")}
                data-testid="sales-order-cancel-confirm"
                onClick={() => {
                  cancel.mutate(reason.trim(), {
                    onSuccess: () => {
                      setConfirming(null);
                      setReason("");
                    },
                  });
                }}
              >
                {t("sales.orders.detail.cancelConfirm")}
              </Button>
              <Button
                size="sm"
                data-testid="sales-order-cancel-keep"
                onClick={() => {
                  setConfirming(null);
                }}
              >
                {t("sales.orders.detail.cancelKeep")}
              </Button>
            </>
          ) : (
            <>
              {canCancel ? (
                <Button
                  variant="danger"
                  size="sm"
                  data-testid="sales-order-cancel"
                  onClick={() => {
                    target({ kind: "order" });
                  }}
                >
                  {t("sales.orders.detail.cancel")}
                </Button>
              ) : null}
              {canDeliver ? (
                <Button
                  size="sm"
                  data-testid="sales-order-mark-delivered"
                  onClick={() => {
                    target({ kind: "deliver" });
                  }}
                >
                  {t("sales.orders.detail.markDelivered")}
                </Button>
              ) : null}
              {canOverride ? (
                <Button
                  size="sm"
                  data-testid="sales-order-override"
                  onClick={() => {
                    target({ kind: "override" });
                  }}
                >
                  {t("sales.orders.detail.override")}
                </Button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Module 03's CTP dialog bound to one order line (D-474 ruling 2, through
 * `planning/public.ts`). It waits for the product so it knows the base unit:
 * the dialog quotes in the base unit, so a line in that unit opens with its
 * ordered quantity and any other opens empty beside the base unit's code.
 */
function OrderLineCheckDialog({
  line,
  facilityId,
  label,
  onCommitted,
  onClose,
}: {
  line: OrderLine;
  facilityId: string | undefined;
  label: string;
  onCommitted: (lineId: string) => void;
  onClose: () => void;
}) {
  const product = useProduct(line.product_id);
  if (product.isPending) return null;
  const inBase = product.data?.base_uom_id === line.uom_id;
  return (
    <CtpDialog
      facilityId={facilityId}
      productId={line.product_id}
      qty={inBase ? line.qty_ordered : undefined}
      line={{ id: line.id, label }}
      onCommitted={() => {
        onCommitted(line.id);
      }}
      onClose={onClose}
    />
  );
}

/**
 * Re-promise one line (spec 09 §4.7, D-469): module 03's CTP dialog through
 * `planning/public.ts`, bound to the line, in its re-promise mode. It opens
 * with the line's open quantity when the line is in the product's base unit,
 * and Re-promise calls module 09's route with the quoted promise and the
 * "Tell the customer" choice, which is off by default.
 */
function OrderLineRePromiseDialog({
  orderId,
  line,
  facilityId,
  label,
  onClose,
}: {
  orderId: string;
  line: OrderLine;
  facilityId: string | undefined;
  label: string;
  onClose: () => void;
}) {
  const product = useProduct(line.product_id);
  const rePromise = useRePromiseLine(orderId);
  if (product.isPending) return null;
  const inBase = product.data?.base_uom_id === line.uom_id;
  const open = inBase ? openQtyText(line.qty_ordered, line.qty_shipped) : undefined;
  return (
    <CtpDialog
      facilityId={facilityId}
      productId={line.product_id}
      qty={open}
      line={{ id: line.id, label }}
      rePromise={{
        oldDate: line.line_promise_date,
        // The dialog closes on success: the line's chip and date on the
        // detail are the confirmation (the route answers the order, which is
        // written straight into the cache).
        submit: async (promiseId, notifyCustomer) => {
          await rePromise(line.id, promiseId, notifyCustomer);
          onClose();
        },
      }}
      onClose={onClose}
    />
  );
}
